import { useCallback } from 'react';

export interface HandleRubricAnswerChangeParams {
  /** The entity ID (code ID or case ID). Accepted as string or number; always sent to API as string. */
  entityId: string | number;
  /** The entity type for API calls */
  entityType: CQA.AttributeEntityType;
  /** Current attributes on the entity */
  attributes: CQA.Attribute[];
  /** The question ID being answered */
  questionId: number;
  /** Exactly one of these three must be provided. */
  answerValueId?: number;
  answerDisplayText?: string;
  spanAnnotationIds?: number[];
  /** IDs of downstream questions whose attributes should be cleared */
  downstreamQuestionIds: number[];
  /**
   * Source SPANS answer row id (not the question id) so two codes answering
   * the same SPANS question can select the same span without colliding.
   */
  spanScopeAttributeId?: number | null;
  /** @deprecated use spanScopeAttributeId. */
  parentQuestionId?: number | null;
}

export const useHandleRubricAnswerChange = (
  onAnswerChange: (params: CQA.UpdateAttributeParams) => void | Promise<void>,
  onDeleteAnswer: (params: CQA.DeleteAttributeParams) => Promise<void>,
  onClearDownstream?: (params: CQA.ClearDownstreamAttributesParams) => void
) => {
  return useCallback(async (params: HandleRubricAnswerChangeParams) => {
    const {
      entityId,
      entityType,
      attributes,
      questionId,
      answerValueId,
      answerDisplayText,
      spanAnnotationIds,
      downstreamQuestionIds,
      spanScopeAttributeId,
      parentQuestionId
    } = params;
    const scopeAttributeId = spanScopeAttributeId ?? parentQuestionId;

    const matchesSpanScope = (a: CQA.Attribute): boolean => {
      const attributeScopeId = a.spanScopeAttributeId ?? a.parentQuestionId;
      if (scopeAttributeId != null) {
        return attributeScopeId === scopeAttributeId;
      }
      return attributeScopeId == null;
    };

    const attributesToDelete = (attributes || [])
      .filter(a => downstreamQuestionIds.includes(a.questionId))
      .filter(matchesSpanScope);

    // Optimistic clear in one dispatch — avoids intermediate renders while
    // the per-attribute DELETEs are in flight.
    if (onClearDownstream && downstreamQuestionIds.length > 0) {
      const clearParams: CQA.ClearDownstreamAttributesParams = {
        entityId: typeof entityId === 'number' ? String(entityId) : entityId,
        entityType,
        questionIds: downstreamQuestionIds
      };
      if (scopeAttributeId != null) {
        clearParams.spanScopeAttributeId = scopeAttributeId;
      }
      onClearDownstream(clearParams);
    }

    for (const attribute of attributesToDelete) {
      const deleteParams: CQA.DeleteAttributeParams = {
        entityId: String(entityId),
        entityType,
        questionId: attribute.questionId
      };
      if (scopeAttributeId != null) {
        deleteParams.spanScopeAttributeId = scopeAttributeId;
      }
      await onDeleteAnswer(deleteParams);
    }

    // SPAN/SPANS cleared (empty array) routes to DELETE; non-empty falls
    // through to the save block below.
    if (spanAnnotationIds != null && spanAnnotationIds.length === 0) {
      const existingAttribute = (attributes || []).find(
        a => a.questionId === questionId && matchesSpanScope(a)
      );
      if (existingAttribute) {
        const deleteParams: CQA.DeleteAttributeParams = {
          entityId: String(entityId),
          entityType,
          questionId
        };
        if (scopeAttributeId != null) {
          deleteParams.spanScopeAttributeId = scopeAttributeId;
        }
        await onDeleteAnswer(deleteParams);
      }
      return;
    }

    if (answerDisplayText != null && answerDisplayText.trim() === '') {
      const matchingAttributes = (attributes || [])
        .filter(a => a.questionId === questionId)
        .filter(matchesSpanScope);
      if (matchingAttributes.length === 1) {
        const deleteParams: CQA.DeleteAttributeParams = {
          entityId: String(entityId),
          entityType,
          questionId
        };
        if (scopeAttributeId != null) {
          deleteParams.spanScopeAttributeId = scopeAttributeId;
        }
        await onDeleteAnswer(deleteParams);
      }
      return;
    }

    const updateParams: CQA.UpdateAttributeParams = {
      entityId: String(entityId),
      entityType,
      questionId
    };
    if (answerDisplayText != null) {
      updateParams.answerDisplayText = answerDisplayText;
    } else if (spanAnnotationIds != null && spanAnnotationIds.length > 0) {
      updateParams.spanAnnotationIds = spanAnnotationIds;
    } else {
      updateParams.answerValueId = answerValueId;
    }
    if (scopeAttributeId != null) {
      updateParams.spanScopeAttributeId = scopeAttributeId;
    }
    await onAnswerChange(updateParams);
  }, [onAnswerChange, onDeleteAnswer, onClearDownstream]);
};

export default useHandleRubricAnswerChange;
